<template>
  <div id="print-page">
    <div class="h-print mb-1">
      <b>
        Please ensure that the margin settings of your print to PDF is set to
        "None"
      </b>
      <el-button
        v-if="showFinalizeButton"
        class="ml-2"
        size="mini"
        :loading="isProcessing"
        type="primary"
        @click="sendPdf"
      >
        {{ finalizeButtonLabel }}
      </el-button>
    </div>
    <div ref="printContent" class="print-page-wrapper">
      <div class="page-content">
        <table id="page-layout">
          <thead>
            <tr>
              <td>
                <div class="header-space">&nbsp;</div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div class="content"><slot></slot></div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div class="footer-space">&nbsp;</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div class="page-footer"></div>
    </div>
  </div>
</template>
<script>
import api from "@/api";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default {
  props: {
    finalizeUrl: {
      type: String,
      default: ""
    },
    finalizePayload: {
      type: Object,
      default: () => ({})
    },
    finalizeButtonLabel: {
      type: String,
      default: "Send PDF"
    },
    showFinalizeButton: {
      type: Boolean,
      default: false
    },
    orientation: {
      type: String,
      default: "portrait",
      validator: v => ["portrait", "landscape"].includes(v)
    }
  },
  data() {
    return {
      isProcessing: false
    };
  },
  mounted() {
    this.$store.dispatch("ui/togglePrintView");
  },
  methods: {
    getFinalizeErrorMessage(error) {
      if (error && error.response && error.response.data) {
        const errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error ||
          error.response.data.msg ||
          error.response.data;
        if (Array.isArray(errorMessage)) {
          return errorMessage.join(", ");
        }
        if (typeof errorMessage === "string") {
          return errorMessage;
        }
        if (
          error.response.data.errors &&
          Array.isArray(error.response.data.errors)
        ) {
          return error.response.data.errors.join(", ");
        }
        if (
          error.response.data.errors &&
          typeof error.response.data.errors === "object"
        ) {
          return Object.values(error.response.data.errors)
            .map(item => (Array.isArray(item) ? item.join(", ") : item))
            .join(", ");
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return "Unable to submit PDF. Please try again.";
    },
    async sendPdf() {
      const printContainer = this.$refs.printContent;
      if (!printContainer) {
        this.$message &&
          this.$message.error("Unable to locate the print content.");
        return;
      }
      if (!this.finalizeUrl) {
        this.$message &&
          this.$message.error(
            "No endpoint configured for sending the PDF. Please provide `finalizeUrl`."
          );
        return;
      }
      let recipientEmail = "";
      try {
        const promptResult = await this.$prompt(
          "Provide the recipient email.",
          "Recipient Email",
          {
            inputValue: this.finalizePayload.email || "",
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel",
            inputPattern: /.+@.+\..+/,
            inputErrorMessage: "Please enter a valid email address."
          }
        );
        recipientEmail = promptResult.value;
      } catch (err) {
        return;
      }
      try {
        await this.$confirm(
          "Are you sure want send to this email ?",
          recipientEmail,
          {
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            type: "warning"
          }
        );
      } catch (err) {
        return;
      }
      this.isProcessing = true;
      try {
        await this.$nextTick();
        const canvas = await html2canvas(printContainer, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          windowWidth: printContainer.scrollWidth,
          windowHeight: printContainer.scrollHeight
        });
        const imgData = canvas.toDataURL("image/png");
        const pdfOrientation = this.orientation === "landscape" ? "l" : "p";
        const pdf = new jsPDF(pdfOrientation, "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const dataUri = pdf.output("datauristring");
        const base64 = dataUri.split(",")[1];
        const payload = {
          ...this.finalizePayload,
          email: recipientEmail,
          form1_pdf: base64
        };
        const response = await api.post(this.finalizeUrl, payload, "json");
        if (
          response &&
          response.data &&
          response.data.code &&
          response.data.code !== 200
        ) {
          const codeMessage = response.data.message || "Failed to submit PDF.";
          this.$message && this.$message.error(codeMessage);
          return;
        }
        this.$message &&
          this.$message.success("PDF generated and submitted successfully.");
      } catch (err) {
        console.error("Failed to generate or submit PDF", err);
        const message = this.getFinalizeErrorMessage(err);
        this.$message && this.$message.error(message);
      } finally {
        this.isProcessing = false;
      }
    }
  }
};
</script>
<style lang="scss">
.footer-divider {
  display: block;
  border-bottom: 1px solid #333;
  border-top: 1px solid #333;
  padding: 2px;
  margin-bottom: 8px;
  margin-top: 8px;
}
.header-text .sub-header {
  width: 100%;
  text-align: center;
  display: block;
  font-size: 10px;
}
#print-page {
  display: flex;
  align-items: center;
  flex-direction: column;
  background: #dadce0;
  padding-top: 30px;
  padding-bottom: 30px;
  font-size: 16px;
  font-family: "Calibri", sans-serif;
  .print-page-wrapper {
    width: 296mm;
    min-height: 209mm;
    padding: 14mm 8mm;
    background: #fff;
  }
  .page-header {
    display: flex;
    font-family: "Times-New-Roman", serif;
    justify-content: center;
    .header-logo {
      width: 60px;
      margin-right: 15px;
      img {
        width: 100%;
      }
    }
    .header-text {
      margin-top: -5px;
      h3 {
        font-size: 24px;
        margin-top: -10px;
        border-bottom: 2px solid #4343a1;
      }
    }
  }
  .footer-content {
    margin-top: 30px;
    font-size: 12px;
  }
  .page-content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    #page-layout {
      width: 100%;
      // margin-top: calc(120px - 72px);
      > thead {
        display: none;
      }
      > tfoot {
        display: none;
      }
    }
  }
}

#extrafooter {
  color: #333;
  a {
    color: #333;
  }
}

.custom-footer {
  font-size: 14px;
}

@media screen {
  .print-page-wrapper {
    display: flex;
    flex-direction: column;
    .page-content {
      flex-grow: 1;
    }
  }
  .page-footer {
    // position: absolute;
    // bottom: 50px;
    // width: 687px;
  }
}

@media print {
  body,
  #app {
    height: 100%;
  }
  #print-page {
    padding-top: 0;
    padding-bottom: 0;
    .page-header {
      width: 100%;
      position: fixed;
      top: 10px;
      left: 0;
      padding-top: 8mm;
      overflow: hidden;
    }
    .page-footer {
      width: 100%;
      position: fixed;
      bottom: 10px;
      left: 0;
      overflow: hidden;
      padding-bottom: 5mm;
      .footer-content {
        margin-top: 0;
      }
      .footer-divider {
        width: 90%;
        margin-left: 5%;
      }
    }
    .print-page-wrapper {
      padding-top: 0;
      padding-bottom: 0;
      width: 100% !important;
      display: flex;
      justify-content: center;
      flex-direction: column;
    }
    // .header-space {
    //   height: 150px;
    // }
    // .footer-space {
    //   height: 115px;
    // }
    #page-layout {
      > thead {
        display: table-header-group !important;
      }
      > tfoot {
        display: table-footer-group !important;
      }
    }
  }
  #extrafooter {
    margin-left: 14mm;
    .ui-icon {
      display: none;
    }
  }
  .custom-footer {
    margin-left: 14mm;
  }
  .h-print {
    display: none;
  }
}

@page {
  size: A4 landscape;
  margin: 0;
}

table {
  page-break-inside: auto;
}
tr {
  page-break-inside: avoid;
  page-break-after: auto;
}
</style>
